import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fetchPriceApparat } from "../../store/actions/paymentsActions";

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

// Каждая позиция — ключ перевода в prices.json + поле в priceApparat
// (см. GET /api/v1/payments/price). Значения по умолчанию — на случай,
// если ответ ещё не пришёл или бэкенд их не прислал.
const ROWS = [
    { key: "black_white_a4", field: "price_for_black_and_white", fallback: 30 },
    { key: "color_a4", field: "price_for_color", fallback: 60 },
    { key: "double_black_white", field: "price_for_black_and_white_duplex", fallback: 50 },
    { key: "double_color", field: "price_for_color_duplex", fallback: 100 },
    { key: "photo_bw", field: "price_for_photo_bw", fallback: 50 },
    { key: "photo_color", field: "price_for_photo_color", fallback: 80 },
    { key: "blank_sheet", field: "price_for_blank_sheet", fallback: 30 },
];

function PricePage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { priceApparat, loadingPayment, error } = useSelector((state) => state.payments);

    useEffect(() => {
        dispatch(fetchPriceApparat());
    }, [dispatch]);

    if (loadingPayment) {
        return (
            <div className="h-[88vh] px-[2.4vw] py-[2vh] flex items-center justify-center bg-[#EDEFF4]">
                <div className="w-[5vh] h-[5vh] border-[0.5vh] border-[#E1E4EB] border-t-[#246CE2] rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-[88vh] px-[2.4vw] py-[2vh] flex items-center justify-center bg-[#EDEFF4]">
                <div className="bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[3vw] py-[3vh] text-center text-[1.8vh] font-semibold text-[#C2543E]">
                    {t("prices.error")}:{" "}
                    {typeof error === "object"
                        ? error.detail || t("prices.error_default")
                        : error}
                </div>
            </div>
        );
    }

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.6vh] bg-[#EDEFF4]">
            <motion.h1
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none text-[2.6vh] font-extrabold tracking-tight"
            >
                {t("prices.title")}
            </motion.h1>

            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-1 min-h-0 bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] p-[2.4vh_1.8vw] grid grid-cols-2 gap-[1.4vh_1.4vw] content-start overflow-y-auto"
            >
                {ROWS.map((row) => (
                    <div
                        key={row.key}
                        className="bg-[#F1F3F7] rounded-[2vh] px-[1.6vw] py-[2vh] flex flex-col items-center text-center gap-[0.8vh]"
                    >
                        <span className="text-[1.5vh] font-bold leading-snug">
                            {t(`prices.${row.key}`)}
                        </span>
                        <span className="text-[2.6vh] font-extrabold text-[#246CE2] leading-none">
                            {priceApparat?.[row.field] ?? row.fallback} ₸
                        </span>
                    </div>
                ))}
            </motion.div>
        </div>
    );
}

export default PricePage;
